import React from "react";
import { Link } from "react-router-dom";
import { Quote, ShieldCheck, MapPin, Mail, Phone, MessageCircle, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { useReveal } from "../../hooks/useReveal";
import { CONTACT, SECURITY_BADGES, SECURITY_LOGOS } from "../../mock";
import { ShareButtons } from "./ShareButtons";

export const Testimonials = () => {
  const { t } = useLanguage();
  const [ref, visible] = useReveal();

  return (
    <section id="clientes" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <h2 className="font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] tracking-tight text-black max-w-3xl">
          {t.testimonials.title}
        </h2>
        <div
          ref={ref}
          className={`mt-12 grid md:grid-cols-3 gap-6 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {t.testimonials.items.map((item, i) => (
            <figure
              key={i}
              data-testid={`testimonial-card-${i}`}
              className="flex flex-col bg-[#fafcf9] border border-black/[0.06] rounded-3xl p-7 hover:shadow-[0_12px_36px_rgba(0,0,0,0.07)] transition-all duration-300 hover:-translate-y-1"
              style={{ transitionDelay: visible ? `${i * 80}ms` : "0ms" }}
            >
              <Quote size={26} className="text-[#0fe814] mb-4" fill="#0fe814" />
              <blockquote className="text-[15px] leading-relaxed text-black/75 font-medium flex-1">
                “{item.quote}”
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-black/[0.07]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-black text-[#0fe814] flex items-center justify-center font-extrabold text-[14px]">
                    {item.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <div className="text-[14px] font-bold text-black">{item.name}</div>
                    <div className="text-[12px] font-medium text-black/45">{item.role}</div>
                  </div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Security = () => {
  const { t } = useLanguage();

  return (
    <section className="py-16 lg:py-20 bg-[#fafcf9] border-y border-black/[0.05]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
        <h2 className="font-extrabold text-[26px] sm:text-[32px] tracking-tight text-black">{t.security.title}</h2>
        <p className="mt-3 text-[15px] text-black/55 font-medium max-w-2xl mx-auto">{t.security.subtitle}</p>

        {/* Certification & partner logos */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4 lg:gap-5">
          {SECURITY_LOGOS.map((logo) => (
            <div
              key={logo.alt}
              data-testid={`security-logo-${logo.alt.split(" ")[0].toLowerCase()}`}
              className="flex items-center justify-center bg-white border border-black/[0.08] rounded-2xl px-7 py-5 shadow-sm hover:shadow-md hover:border-[#0fb013]/40 transition-all duration-300 hover:-translate-y-0.5"
            >
              <img src={logo.src} alt={logo.alt} className={`${logo.h} w-auto object-contain`} draggable="false" />
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {SECURITY_BADGES.map((badge) => (
            <span
              key={badge}
              className="inline-flex items-center gap-2 text-[13px] font-bold text-black/70 bg-white border border-black/[0.08] px-5 py-3 rounded-full shadow-sm hover:border-[#0fb013]/50 hover:text-black transition-colors duration-200"
            >
              <ShieldCheck size={16} className="text-[#0fb013]" />
              {badge}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Contact = () => {
  const { t } = useLanguage();
  const [ref, visible] = useReveal();

  const cards = [
    {
      icon: MapPin,
      label: t.contact.addressLabel,
      value: CONTACT.address,
      testid: "contact-address",
    },
    {
      icon: Mail,
      label: t.contact.emailLabel,
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      cta: t.contact.emailCta,
      testid: "contact-email",
    },
    {
      icon: Phone,
      label: t.contact.phoneLabel,
      value: CONTACT.phone,
      href: CONTACT.phoneHref,
      cta: t.contact.callCta,
      testid: "contact-phone",
    },
    {
      icon: MessageCircle,
      label: t.contact.whatsappLabel,
      value: CONTACT.whatsapp,
      href: CONTACT.whatsappHref,
      cta: t.contact.whatsappCta,
      external: true,
      testid: "contact-whatsapp",
    },
  ];

  return (
    <section id="contacto" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <h2 className="font-extrabold text-[28px] sm:text-[36px] lg:text-[42px] tracking-tight text-black">
          {t.contact.title}
        </h2>
        <p className="mt-4 text-[16px] text-black/55 font-medium max-w-2xl">{t.contact.subtitle}</p>

        <div
          ref={ref}
          className={`mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div
                key={i}
                data-testid={card.testid}
                className="flex flex-col bg-[#fafcf9] border border-black/[0.06] rounded-2xl p-6 hover:border-[#0fe814]/50 hover:shadow-[0_12px_32px_rgba(15,176,19,0.10)] transition-all duration-300"
                style={{ transitionDelay: visible ? `${i * 60}ms` : "0ms" }}
              >
                <div className="w-11 h-11 rounded-xl bg-black flex items-center justify-center mb-5">
                  <Icon size={20} className="text-[#0fe814]" />
                </div>
                <div className="text-[12px] font-bold uppercase tracking-wider text-black/40">{card.label}</div>
                <div className="mt-1.5 text-[14px] font-semibold text-black leading-relaxed flex-1">{card.value}</div>
                {card.href && (
                  <a
                    href={card.href}
                    target={card.external ? "_blank" : undefined}
                    rel={card.external ? "noopener noreferrer" : undefined}
                    className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#0fb013] hover:text-black transition-colors duration-200"
                  >
                    {card.cta} <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLanguage();

  const cols = [
    { title: t.footer.products, links: t.footer.productLinks },
    { title: t.footer.company, links: t.footer.companyLinks },
    { title: t.footer.resources, links: t.footer.resourceLinks },
    {
      title: t.footer.legal,
      links: t.footer.legalLinks,
      routes: ["/terminos-y-condiciones", "/politicas-de-privacidad"],
    },
  ];

  return (
    <footer className="bg-black text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-6 gap-10">
          <div className="lg:col-span-2">
            {/* White logo for dark background */}
            <img
              src="/logos/rapidpay-white.png"
              alt="RapidPay"
              className="h-10 w-auto"
              draggable="false"
            />
            <p className="mt-4 text-[13px] leading-relaxed text-white font-medium max-w-xs">{t.footer.tagline}</p>
            <div className="mt-6 space-y-2.5 text-[12px] font-medium text-white">
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-[#0fe814] mt-0.5 shrink-0" />
                <span>{CONTACT.address}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-[#0fe814] shrink-0" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-[#0fe814] transition-colors duration-200">{CONTACT.email}</a>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-[#0fe814] shrink-0" />
                <a href={CONTACT.phoneHref} className="hover:text-[#0fe814] transition-colors duration-200">{CONTACT.phone}</a>
              </div>
              <div className="flex items-center gap-2">
                <MessageCircle size={14} className="text-[#0fe814] shrink-0" />
                <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" className="hover:text-[#0fe814] transition-colors duration-200">
                  WhatsApp: {CONTACT.whatsapp}
                </a>
              </div>
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title}>
              <h4 className="text-[13px] font-bold uppercase tracking-wider text-white mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map((link, li) => (
                  <li key={link}>
                    {col.routes && col.routes[li] ? (
                      <Link
                        to={col.routes[li]}
                        data-testid={`footer-legal-link-${li}`}
                        className="text-[12px] font-medium text-white hover:text-[#0fe814] transition-colors duration-200 text-left"
                      >
                        {link}
                      </Link>
                    ) : (
                      <button className="text-[12px] font-medium text-white hover:text-[#0fe814] transition-colors duration-200 text-left">
                        {link}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col lg:flex-row items-center justify-between gap-4">
          <span className="text-[11px] font-medium text-white">
            © {new Date().getFullYear()} RapidPay. {t.footer.rights}
          </span>
          <ShareButtons />
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#0fe814]" />
            <span className="text-[11px] font-medium text-white">PCI DSS Level 2 Certified</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
